import { useEffect, useMemo, useState } from 'react'
import { TreeSelect } from 'antd'
import { CheckCircleOutlined } from '@ant-design/icons'
import { green } from '@ant-design/colors'
import { centsToDollarsDisplay } from '../../displayFormatters'
import { VisitFilter } from '../TimeAnalysis/timeAnalysisModel'
import { pluralize } from '../../../helpers/strings'

type AnalysisVisitData = {
  compoundId: string
  workareaId: number
  date: string
  time: string
  price: number
  billed: boolean
}

type AnalysisWorkarea = {
  id: number
  name: string
  isOnHold: boolean
}

type Props = {
  workareas?: AnalysisWorkarea[]
  visits?: AnalysisVisitData[]
  onChange: (enabledWorkareaIds: number[], visitFilters: VisitFilter[]) => void
}

const formatDate = (dateStr: string) => {
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) {
    return dateStr
  }
  return d.toLocaleDateString()
}

const AnalysisFilter = ({ workareas = [], visits = [], onChange }: Props) => {
  const treeData = useMemo(() => {
    return workareas.map(wa => {
      const waVisits = visits.filter(v => v.workareaId === wa.id)
      return {
        title: wa.isOnHold ? `${wa.name} (On Hold)` : wa.name,
        value: `wa_${wa.id}`,
        key: `wa_${wa.id}`,
        disabled: wa.isOnHold,
        children: waVisits.map(v => ({
          title: '',
          value: v.compoundId,
          key: v.compoundId,
          disabled: wa.isOnHold,
          date: v.date,
          time: v.time,
          price: v.price,
          billed: v.billed,
        })),
      }
    })
  }, [workareas, visits])

  const allCheckableValues = useMemo(() => {
    const values: string[] = []
    workareas.forEach(wa => {
      if (wa.isOnHold) {
        return
      }
      values.push(`wa_${wa.id}`);
      visits.filter(v => v.workareaId === wa.id).forEach(v => {
        values.push(v.compoundId)
      })
    })
    return values
  }, [workareas, visits])

  const [checkedValues, setCheckedValues] = useState<string[]>([])

  useEffect(() => {
    setCheckedValues(allCheckableValues)
  }, [allCheckableValues])

  const deriveFilters = (values: string[]) => {
    const checkedVisitIds = new Set(values.filter(v => !v.startsWith('wa_')))

    const enabledWorkareaIds = workareas
      .filter(wa => !wa.isOnHold)
      .filter(wa => {
        const waVisits = visits.filter(v => v.workareaId === wa.id)
        if (waVisits.length === 0) {
          return values.includes(`wa_${wa.id}`)
        }
        return waVisits.some(v => checkedVisitIds.has(v.compoundId))
      })
      .map(wa => wa.id)

    const visitFilters: VisitFilter[] = visits.map(v => ({
      compoundId: v.compoundId,
      include: checkedVisitIds.has(v.compoundId),
    }))

    return { enabledWorkareaIds, visitFilters }
  }

  const handleChange = (newValues: string[]) => {
    setCheckedValues(newValues)
    const { enabledWorkareaIds, visitFilters } = deriveFilters(newValues)
    onChange(enabledWorkareaIds, visitFilters)
  }

  const treeTitleRender = (nodeData: any) => {
    if (String(nodeData.value).startsWith('wa_')) {
      return <span>{nodeData.title}</span>
    }

    const reviewedIcon = nodeData.billed
      ? <CheckCircleOutlined style={{ color: green.primary }} />
      : <></>

    return (
      <span>
        {formatDate(nodeData.date)} &middot; {nodeData.time}h &middot; {centsToDollarsDisplay(nodeData.price)} {reviewedIcon}
      </span>
    )
  }

  return (
    <div>
      <b>
        <i className="fas fa-fw fa-user-hard-hat" /> Workarea &amp; Visit Analysis
      </b>
      <hr />
      <TreeSelect
        treeData={treeData}
        value={checkedValues}
        onChange={handleChange}
        treeCheckable
        showCheckedStrategy={TreeSelect.SHOW_ALL}
        placeholder="Select workareas and visits"
        treeTitleRender={treeTitleRender}
        style={{ width: 400 }}
        maxTagCount={0}
        maxTagPlaceholder={() => {
          const { enabledWorkareaIds, visitFilters } = deriveFilters(checkedValues)
          const visitCount = visitFilters.filter(v => v.include).length
          return `${enabledWorkareaIds.length} ${pluralize(enabledWorkareaIds.length, 'Work Area')}, ${visitCount} ${pluralize(visitCount, 'Visit')}`
        }}
      />
    </div>
  )
}

export default AnalysisFilter
